import { CircularProgress } from '@material-ui/core';
import Search from '@material-ui/icons/Search';
import { useRouter } from 'next/router';
import { useRef, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { doEnableAgentTips, PageLinks, ScrollStates, SearchStatus } from '../../../../constants';
import isElementInViewport from '../../../../helpers/isElementInViewport';
import useOnScrollStop from '../../../../hooks/useOnScrollStop';
import { getAgentTipsRequest } from '../../../../store/slices/intelligence';
import AgentTips from '../../../AgentTips/AgentTips';

const AgentTipsInline = ({
  canShowAgentTips,
  itineraryId,
  cruiseDepartDate,
  cruiseReturnDate,
  departureCityId,
  selectedItineraryRow,
  handleDoShowAgentTip,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { doEnableAgentTips: doEnableAgentTipsFromQueryString, pageName } = router.query;
  const { doRunTipsInParallel } = router.query;
  // THIS IS WHERE YOU CAN TOGGLE TIPS
  let doEnableAgentTipsFeature = doEnableAgentTips || doEnableAgentTipsFromQueryString === 'true';
  if (pageName === PageLinks.ChangeCruise.pageName || pageName === PageLinks.AddCruise.pageName) {
    doEnableAgentTipsFeature = false;
  }
  const searchObj = useSelector((state) => state.tripSearch.searchObj.cruise);
  const { value: flightOriginGeoIndex, featureClass } = searchObj.filters.origin || {};
  const hasFlightOrigin = flightOriginGeoIndex && !!featureClass;
  const agentTipsSearching = useSelector((state) => state.intelligence.agentTips[`${itineraryId}`]);
  const agentTipsSuccess = useSelector(
    (state) => state.intelligence.agentTips[`${itineraryId}-${flightOriginGeoIndex}`],
  );
  const isAgentTipsSearching =
    !!agentTipsSearching && agentTipsSearching.status === SearchStatus.SEARCHING;
  const scrollState = useSelector((store) => store.scroll || {});
  const { onRefSet } = useOnScrollStop();
  const viewRulesEl = useRef(null);

  const getAgentTips = () => {
    if (!isAgentTipsSearching && hasFlightOrigin && doEnableAgentTipsFeature) {
      dispatch(
        getAgentTipsRequest({
          cruiseDepartDate,
          cruiseReturnDate,
          cruiseItineraryId: itineraryId,
          cruiseDepartureCityId: departureCityId,
          doRunTipsInParallel,
        }),
      );
    }
  };

  const handleLoadMoreAgentTips = () => {
    getAgentTips();
  };

  useEffect(() => {
    if (
      canShowAgentTips &&
      (scrollState.status === ScrollStates.DONE || scrollState.status === null) &&
      isElementInViewport(viewRulesEl.current)
    ) {
      getAgentTips();
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [agentTipsSuccess, scrollState.status, agentTipsSearching, canShowAgentTips]);

  useEffect(() => {
    onRefSet('body');
  }, [onRefSet]);

  return (
    <>
      {canShowAgentTips && !agentTipsSuccess && doEnableAgentTipsFeature && (
        <div className="mx-10 my-5 mb-5" ref={viewRulesEl}>
          <div className="text-s font-bold mb-3">Agent tips</div>
          {hasFlightOrigin && (
            <button
              onClick={handleLoadMoreAgentTips}
              type="button"
              className="border border-theme block rounded-pill text-center rounded-2xl text-xs text-white py-3 w-full font-bold text-theme focus:outline-none"
            >
              {isAgentTipsSearching ? (
                <CircularProgress color="inherit" size={15} />
              ) : (
                <div>
                  <Search fontSize="small" className="mr-1" />
                  Explore tips to find better deals on cruise+flight+hotel
                </div>
              )}
            </button>
          )}
          {!hasFlightOrigin && (
            <span>
              Please specify a flight origin in filters above to use the Agent Tips feature.
            </span>
          )}
        </div>
      )}
      {canShowAgentTips && agentTipsSuccess && doEnableAgentTipsFeature && (
        <AgentTips
          itineraryId={selectedItineraryRow.itineraryId}
          handleDoShowAgentTip={handleDoShowAgentTip}
          agentTips={agentTipsSuccess}
        />
      )}
    </>
  );
};

AgentTipsInline.propTypes = {
  canShowAgentTips: PropTypes.bool.isRequired,
  itineraryId: PropTypes.string.isRequired,
  cruiseDepartDate: PropTypes.string.isRequired,
  cruiseReturnDate: PropTypes.string.isRequired,
  departureCityId: PropTypes.number.isRequired,
  selectedItineraryRow: PropTypes.objectOf(PropTypes.objectOf).isRequired,
  handleDoShowAgentTip: PropTypes.func,
};

AgentTipsInline.defaultProps = {
  handleDoShowAgentTip: undefined,
};

export default AgentTipsInline;
